import { Link, useRouterState } from "@tanstack/react-router";
import { ShieldCheck } from "lucide-react";

import { cn } from "@/lib/cn";
import { ADMIN_GROUPS, sectionFor } from "@/pages/admin/sections";

import { useAdminSections } from "./consolePages";

/** The admin console's own menu, beside the (collapsed) main menu. */
export function ConsoleNav() {
  const sections = useAdminSections();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const current = sectionFor(path).id;
  return (
    <div className="hidden w-[232px] shrink-0 border-r border-border bg-surface lg:block">
      <nav
        aria-label="Admin console"
        className="sticky top-[var(--shell-top)] h-[calc(100dvh-var(--shell-top))] overflow-y-auto px-3 py-4"
      >
        <div className="mb-2 flex items-center gap-2.5 border-b border-border px-2 pb-3">
          <span className="grid size-8 place-items-center rounded-[9px] bg-gradient-to-br from-[#2f66ff] to-[#6d4fd8] text-white">
            <ShieldCheck className="size-4" />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-fg">Admin console</div>
            <div className="text-[11.5px] text-subtle">
              Every change is audit-logged
            </div>
          </div>
        </div>
        {ADMIN_GROUPS.map((group) => {
          const items = sections.filter((s) => s.group === group);
          if (!items.length) return null;
          return (
            <div key={group} className="mt-3">
              <div className="mb-1 px-2 text-[10.5px] font-semibold tracking-[0.09em] text-subtle uppercase">
                {group === "Overview" ? "Console" : group}
              </div>
              <ul className="grid gap-0.5">
                {items.map((s) => {
                  const Icon = s.icon;
                  const on = s.id === current;
                  return (
                    <li key={s.id}>
                      <Link
                        to={s.path as never}
                        aria-current={on ? "page" : undefined}
                        className={cn(
                          "flex h-9 items-center gap-2.5 rounded-[10px] px-2.5 text-[13.5px] text-muted transition-colors hover:bg-surface-2 hover:text-fg",
                          on && "bg-brand-soft font-semibold !text-brand",
                        )}
                      >
                        <Icon className="size-4 shrink-0" />
                        <span className="truncate">{s.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
    </div>
  );
}

/** Phones and the top-menu layout: the admin pages as a scrolling row of tabs. */
export function ConsoleTabs({ className }: { className?: string }) {
  const sections = useAdminSections();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const current = sectionFor(path).id;
  return (
    <nav
      aria-label="Admin pages"
      className={cn("border-b border-border bg-surface", className)}
    >
      <ul className="flex gap-1 overflow-x-auto px-3 sm:px-5">
        {sections.map((s) => {
          const on = s.id === current;
          return (
            <li key={s.id} className="shrink-0">
              <Link
                to={s.path as never}
                aria-current={on ? "page" : undefined}
                className={cn(
                  "relative inline-flex h-11 items-center px-3 text-sm whitespace-nowrap transition-colors",
                  on ? "font-semibold text-brand" : "text-muted hover:text-fg",
                )}
              >
                {s.label}
                {on ? (
                  <span
                    className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-brand"
                    aria-hidden
                  />
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
